import { z } from 'zod';

// --- Review Queue Optimiser schemas (output contracts) ---

export const ReviewQueuePreferencesSchema = z.object({
  /** 0 = pure impact/value, 1 = pure flow/throughput */
  flowVsImpact: z.number().min(0).max(1),
  wipLimit: z.number().int().positive().optional(),
  sprintEndDate: z.string().optional(),
});

const ReviewQueuePrInputSchema = z.object({
  number: z.number().int(),
  title: z.string(),
  state: z.string(),
  author: z.union([z.string(), z.object({ login: z.string() })]),
  reviews: z.array(
    z.object({
      id: z.string(),
      state: z.string(),
      submittedAt: z.string().nullable(),
      author: z.union([z.string(), z.object({ login: z.string() })]),
    }),
  ),
  body: z.string(),
  headRefName: z.string(),
  createdAt: z.string(),
  mergedAt: z.string().nullable(),
  additions: z.number(),
  deletions: z.number(),
  files: z.array(
    z.object({
      path: z.string(),
      additions: z.number(),
      deletions: z.number(),
    }),
  ),
  commits: z.array(z.object({ date: z.string() })),
  isDraft: z.boolean(),
  reviewRequests: z.array(z.object({ login: z.string() })),
  approvalsRequired: z.number().int().nonnegative(),
  ciStatus: z.enum(['pass', 'fail', 'pending']),
  dependsOn: z.array(z.number().int()),
  deadline: z.string().optional(),
  deadlineType: z.enum(['hard', 'soft', 'none']).optional(),
  stackId: z.string().optional(),
  stackPosition: z.number().optional(),
  businessValue: z.number().optional(),
  complexity: z.number().optional(),
  ciFailureRate: z.number().optional(),
  mergeQueuePosition: z.number().optional(),
});

const ReviewQueuePersonInputSchema = z
  .object({
    login: z.string(),
    avgReviewTimeHours: z.number(),
    reviewTimeStddev: z.number(),
    tzOffset: z.number(),
    activeHoursStart: z.number(),
    activeHoursEnd: z.number(),
    knowledgeCache: z.record(z.string(), z.number()),
    historicalReviewAccuracy: z.number(),
    availabilityOverrides: z.array(
      z.object({
        startIso: z.string(),
        endIso: z.string(),
        factor: z.number(),
      }),
    ),
    codeownerPaths: z.array(z.string()),
    maxConcurrentAuthoring: z.number(),
    reviewHistory: z.array(
      z.object({
        prNumber: z.number().int(),
        files: z.array(z.string()),
        timestampIso: z.string(),
      }),
    ),
  })
  .transform((person) => ({
    ...person,
    knowledgeCache: new Map(Object.entries(person.knowledgeCache)),
  }));

/** Action-specific scenarios accepted by both HTTP and MCP queue surfaces. */
export const ReviewQueueHypotheticalSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pr_removed'),
    params: z.object({ prNumber: z.number().int() }),
  }),
  z.object({
    type: z.literal('pr_added'),
    params: z.object({ pr: ReviewQueuePrInputSchema }),
  }),
  z.object({
    type: z.literal('person_unavailable'),
    params: z.object({
      login: z.string(),
      startIso: z.string(),
      endIso: z.string(),
    }),
  }),
  z.object({
    type: z.literal('deadline_changed'),
    params: z.object({
      prNumber: z.number().int(),
      deadline: z.string(),
      deadlineType: z.enum(['hard', 'soft', 'none']),
    }),
  }),
  z.object({
    type: z.literal('reviewer_reassigned'),
    params: z.object({
      prNumber: z.number().int(),
      fromLogin: z.string(),
      toLogin: z.string(),
    }),
  }),
  z.object({
    type: z.literal('pr_split'),
    params: z.object({
      originalPrNumber: z.number().int(),
      splitA: ReviewQueuePrInputSchema,
      splitB: ReviewQueuePrInputSchema,
    }),
  }),
  z.object({
    type: z.literal('team_member_added'),
    params: z.object({ person: ReviewQueuePersonInputSchema }),
  }),
]);

const UrgencyLevelSchema = z.enum(['critical', 'high', 'medium', 'low']);

export const ReviewQueueItemSchema = z.object({
  prNumber: z.number().int(),
  title: z.string(),
  author: z.string(),
  mergePosition: z.number().int().positive(),
  estimatedMergeHours: z.number().nonnegative(),
  valueScore: z.number(),
  conflictRisk: z.number().min(0).max(1),
  urgencyLevel: UrgencyLevelSchema,
  nextAction: z.string(),
  rationale: z.string(),
  suggestedReviewer: z.string().nullable(),
});

export const ReassignmentSuggestionSchema = z.object({
  prNumber: z.number().int(),
  fromReviewer: z.string(),
  toReviewer: z.string(),
  timeSavedHours: z.number(),
  cascadeBenefit: z.number(),
  netScore: z.number(),
  rationale: z.string(),
});

export const PersonPrViewSchema = z.object({
  prNumber: z.number().int(),
  title: z.string(),
  action: z.enum([
    'merge_now',
    'fix_ci',
    'mark_ready',
    'chase_review',
    'split_or_simplify',
    'wait_for_ci',
    'wait',
  ]),
  urgency: UrgencyLevelSchema,
  teamRank: z.number().int(),
  rationale: z.string(),
});

export const DayForecastSchema = z.object({
  dayIndex: z.number().int().min(0),
  load: z.number().nonnegative(),
});

export const PersonViewSchema = z.object({
  login: z.string(),
  authoredPrs: z.array(PersonPrViewSchema),
  reviewQueue: z.array(PersonPrViewSchema),
  recommendation: z.string(),
  load: z.number().nonnegative(),
  fiveDayForecast: z.array(DayForecastSchema),
});

export const NewWorkDecisionSchema = z.discriminatedUnion('allowed', [
  z.object({
    allowed: z.literal(true),
    reason: z.literal('CLEAR'),
    detail: z.string(),
  }),
  z.object({
    allowed: z.literal(false),
    reason: z.enum([
      'WIP_LIMIT',
      'LOAD_THRESHOLD',
      'REVIEW_DEBT',
      'SPRINT_COMMITMENT',
    ]),
    detail: z.string(),
    landFirstPrNumber: z.number().int().optional(),
    reviewFirstPrNumbers: z.array(z.number().int()).optional(),
  }),
]);

export const StackAnalysisSchema = z.object({
  stackId: z.string(),
  prNumbers: z.array(z.number().int()),
  totalValue: z.number(),
  totalTtmHours: z.number().nonnegative(),
  baseTtmHours: z.number().nonnegative(),
  health: z.enum(['healthy', 'ci_broken', 'review_stalled', 'blocked']),
  recommendedAction: z.enum([
    'merge_base',
    'fix_base_ci',
    'chase_base_review',
    'redirect_review_to_base',
    'wait',
  ]),
});

export const ReviewQueueWhatIfImpactSchema = z.object({
  valueDelta: z.number(),
  throughputDelta: z.number(),
  avgTtmDelta: z.number(),
  deadlineMissesDelta: z.number().int(),
  loadGiniDelta: z.number(),
});

export const ReviewQueueWhatIfResultSchema = z.object({
  baseSchedulePrNumbers: z.array(z.number().int()),
  altSchedulePrNumbers: z.array(z.number().int()),
  impact: ReviewQueueWhatIfImpactSchema,
  recommendations: z.array(z.string()),
});

export const ReviewQueueResultSchema = z.object({
  orderedQueue: z.array(ReviewQueueItemSchema),
  reassignments: z.array(ReassignmentSuggestionSchema),
  personViews: z.array(PersonViewSchema),
  teamLoadGini: z.number().min(0).max(1),
  totalProjectedHours: z.number().nonnegative(),
  computedAt: z.string(),
  preferences: ReviewQueuePreferencesSchema,
});

const REPO_PATTERN = /^[\w.-]+\/[\w.-]+$/;

/** Input schema for the review-queue API route */
export const ReviewQueueRequestSchema = z.object({
  repos: z
    .array(z.string().regex(REPO_PATTERN, 'must match owner/name'))
    .min(1)
    .refine(
      (repos) => {
        const owner = repos[0]?.split('/')[0]?.toLowerCase();
        return repos.every(
          (repo) => repo.split('/')[0]?.toLowerCase() === owner,
        );
      },
      { message: 'all repos must share the same owner' },
    ),
  preferences: ReviewQueuePreferencesSchema.optional(),
  /** What-if hypothetical scenarios to evaluate instead of the base schedule */
  whatIf: z.array(ReviewQueueHypotheticalSchema).optional(),
});
